import React, { useEffect, useState } from 'react';
import { Result, Button, Typography, Spin, Steps, Card, Space } from 'antd';
import {
    CheckCircleOutlined,
    CloseCircleOutlined,
    LoadingOutlined,
    CreditCardOutlined,
    ShoppingOutlined
} from '@ant-design/icons';
import { useLocation, Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { getPayment } from './PaymentStatusServices';

const { Title, Text } = Typography;
const { Step } = Steps;

const PaymentStatus = () => {
    const location = useLocation();
    const { t } = useTranslation();
    const [paymentStatus, setPaymentStatus] = useState('checking');
    const [sessionId, setSessionId] = useState('');
    const [paymentDetails, setPaymentDetails] = useState(null);

    useEffect(() => {
        const queryParams = new URLSearchParams(location.search);
        const sessionIdParam = queryParams.get('payment_id');

        if (sessionIdParam) {
            setSessionId(sessionIdParam);
            fetchPaymentDetails(sessionIdParam);
        } else {
            setPaymentStatus('error');
        }
    }, [location.search]);

    const fetchPaymentDetails = async (paymentId) => {
        try {
            const response = await getPayment(paymentId);
            if (response.success) {
                setPaymentDetails(response.data);
                setPaymentStatus('success');
            } else {
                setPaymentStatus('error');
            }
        } catch (error) {
            setPaymentStatus('error');
        }
    };

    const containerStyle = {
        background: 'linear-gradient(135deg, #f5f7fa 0%, #c3cfe2 100%)',
        minHeight: '100vh',
        padding: '40px 20px',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center'
    };

    const resultStyle = {
        transition: 'all 0.3s ease-in-out',
        padding: '40px',
        borderRadius: '16px',
        boxShadow: '0 8px 24px rgba(0,0,0,0.1)',
        background: 'rgba(255, 255, 255, 0.95)',
        width: '100%',
        maxWidth: '1200px'
    };

    const renderContent = () => {
        switch (paymentStatus) {
            case 'success':
                return (
                    <div style={resultStyle}>
                        <Result
                            icon={<CheckCircleOutlined style={{ color: '#52c41a', fontSize: '72px' }} />}
                            title={
                                <Title level={2} style={{ color: '#2f54eb' }}>
                                    {t('payment_successful')} 🎉
                                </Title>
                            }
                            subTitle={
                                <Text type="secondary" style={{ fontSize: '16px' }}>
                                    {t('payment_confirmation_email_sent')}
                                </Text>
                            }
                            extra={[
                                <Button
                                    type="primary"
                                    key="dashboard"
                                    size="large"
                                    style={{ padding: '0 32px', height: '48px' }}
                                >
                                    <Link to="/">{t('go_to_home')}</Link>
                                </Button>,
                            ]}
                        >
                            <Space direction="vertical" size="large" style={{ width: '100%', marginTop: '40px' }}>
                                <Steps current={2} style={{ maxWidth: '600px', margin: '0 auto' }}>
                                    <Step title={t('cart')} icon={<ShoppingOutlined />} />
                                    <Step title={t('payment')} icon={<CreditCardOutlined />} />
                                    <Step title={t('complete')} icon={<CheckCircleOutlined />} />
                                </Steps>

                                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))', gap: '24px' }}>
                                    <Card title={t('order_summary')} bordered={false}>
                                        <Space direction="vertical">
                                            <Text>{t('order_id')}: #{sessionId}</Text>
                                            <Text>{t('amount')}: ${paymentDetails?.total}</Text>
                                            <Text>{t('date')}: {new Date(paymentDetails?.payment_date).toLocaleDateString()}</Text>
                                        </Space>
                                    </Card>

                                    <Card title={t('billing_information')} bordered={false}>
                                        <Space direction="vertical">
                                            <Text>{t('address_line1')}: {paymentDetails?.billing_address?.address_line1}</Text>
                                            <Text>{t('address_line2')}: {paymentDetails?.billing_address?.address_line2}</Text>
                                            <Text>{t('city')}: {paymentDetails?.billing_address?.city}</Text>
                                            <Text>{t('country')}: {paymentDetails?.billing_address?.country}</Text>
                                            <Text>{t('postal_code')}: {paymentDetails?.billing_address?.postal_code}</Text>
                                        </Space>
                                    </Card>
                                </div>
                            </Space>
                        </Result>
                    </div>
                );

            case 'error':
                return (
                    <div style={resultStyle}>
                        <Result
                            icon={<CloseCircleOutlined style={{ color: '#ff4d4f', fontSize: '72px' }} />}
                            title={
                                <Title level={2} style={{ color: '#eb2f2f' }}>
                                    {t('payment_failed')} 😢
                                </Title>
                            }
                            subTitle={
                                <Text type="secondary" style={{ fontSize: '16px' }}>
                                    {t('payment_failed_description')}
                                </Text>
                            }
                            extra={[
                                <Button
                                    type="primary"
                                    danger
                                    key="retry"
                                    size="large"
                                    style={{ padding: '0 32px', height: '48px' }}
                                >
                                    <Link to="/checkout">{t('try_again')}</Link>
                                </Button>,
                                <Button
                                    key="support"
                                    size="large"
                                    style={{ height: '48px' }}
                                >
                                    {t('contact_support')}
                                </Button>
                            ]}
                        >
                            <div style={{ marginTop: '40px', textAlign: 'center' }}>
                                <Card title={t('troubleshooting_tips')} bordered={false}>
                                    <Space direction="vertical" align="start">
                                        <Text>• {t('check_card_details')}</Text>
                                        <Text>• {t('ensure_sufficient_funds')}</Text>
                                        <Text>• {t('try_different_payment_method')}</Text>
                                        <Text>• {t('contact_bank_for_authorization')}</Text>
                                    </Space>
                                </Card>
                            </div>
                        </Result>
                    </div>
                );

            default:
                return (
                    <div style={{ textAlign: 'center', padding: '80px 0', ...resultStyle }}>
                        <Spin
                            indicator={<LoadingOutlined style={{ fontSize: 64 }} spin />}
                            tip={
                                <Text strong style={{ fontSize: '18px', marginTop: '24px' }}>
                                    {t('processing_payment')}
                                </Text>
                            }
                        />
                        <div style={{ marginTop: '40px' }}>
                            <Text type="secondary">{t('do_not_close_window')}</Text>
                        </div>
                    </div>
                );
        }
    };

    return (
        <div style={containerStyle}>
            {renderContent()}
        </div>
    );
};

export default PaymentStatus;